import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Button } from 'src/components/shared/Button'
import { TabsSelector } from 'src/components/shared/tabs-selector/TabsSelector'
import type { TabsSelectorOption } from 'src/components/shared/tabs-selector/TabsSelector.types'
import { WebhookDeliveriesFilters } from 'src/components/webhook/deliveries/WebhookDeliveriesFilters'
import type { WebhookDeliveryFilterValues } from 'src/components/webhook/deliveries/WebhookDeliveriesFilters'
import { WebhookDeliveriesTable } from 'src/components/webhook/deliveries/WebhookDeliveriesTable'
import { WebhookDeliveryDetailsModal } from 'src/components/webhook/deliveries/WebhookDeliveryDetailsModal'
import { WebhookSecretModal } from 'src/components/webhook/secret/WebhookSecretModal'
import { SendTestWebhookModal } from 'src/components/webhook/send-test/SendTestWebhookModal'
import type { SendTestWebhookFormValues } from 'src/components/webhook/send-test/SendTestWebhookModal.interface'
import { WebhookSubscriptionFormModal } from 'src/components/webhook/subscription-form/WebhookSubscriptionFormModal'
import type {
  WebhookSubscriptionFormMode,
  WebhookSubscriptionFormValues,
} from 'src/components/webhook/subscription-form/WebhookSubscriptionForm.interface'
import { WebhookSubscriptionsTable } from 'src/components/webhook/subscriptions-table/WebhookSubscriptionsTable'
import { WebhookSubscriptionsTableToolbar } from 'src/components/webhook/subscriptions-table/WebhookSubscriptionsTableToolbar'
import {
  AdminCreateWebhookSubscription,
  AdminFetchWebhookDeliveries,
  AdminFetchWebhookSubscriptions,
  AdminSendWebhook,
  AdminUpdateWebhookSubscription,
  ClearAdminWebhookDeliveries,
} from 'src/constants/actionTypes'
import { webhookDeliveryPageSizeOptions } from 'src/constants/webhook/constants'
import type { WebhookEventType } from 'src/constants/webhook/enums'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type {
  CreateWebhookSubscriptionResult,
  SendWebhookResult,
  WebhookDelivery,
  WebhookSubscription,
} from 'src/interfaces/webhook'
import {
  getWebhookDeliveriesError,
  getWebhookSaving,
  getWebhookSubscriptionsError,
} from 'src/selectors/webhook'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { GSIcon } from 'src/components/shared/GSIcon'

interface ModalState {
  mode: WebhookSubscriptionFormMode
  subscription: WebhookSubscription | null
}

// `'any'` is the unset state for each select — a placeholder cannot be re-selected, so an
// explicit "Any" option is the only way the admin can clear a single filter without the Clear
// button resetting all four. Never send the literal `'any'` to the API.
const INITIAL_FILTERS: WebhookDeliveryFilterValues = {
  subscriptionId: 'any',
  status: 'any',
  eventType: 'any',
  resourceId: '',
}

type WebhooksTab = 'subscriptions' | 'deliveries'

// Two things to look at: what the customer listens to, and what GigSafe has sent them. The
// manual test send is an action on the Deliveries tab (its result lands there), not a tab.
const WEBHOOKS_TABS: TabsSelectorOption<WebhooksTab>[] = [
  { label: 'Subscriptions', value: 'subscriptions' },
  { label: 'Deliveries', value: 'deliveries' },
]

const CustomerWebhooksPageComponent = () => {
  // Each section is rendered — and fetched — only with its own view permission (spec §6).
  const canViewSubscriptions = useHasPermission(InternalPermission.WebhookSubscriptionsView)
  const canViewDeliveries = useHasPermission(InternalPermission.WebhookDeliveriesView)
  const canManageSubscriptions = useHasPermission(InternalPermission.WebhookSubscriptionsManage)
  const canSendWebhooks = useHasPermission(InternalPermission.WebhooksSend)
  const canViewProducts = useHasPermission(InternalPermission.PartnerProductsView)
  // The route needs one of the two view permissions (spec §5.2), so the fallback is readable.
  const [selectedTab, setSelectedTab] = useState<WebhooksTab>(
    canViewSubscriptions ? 'subscriptions' : 'deliveries',
  )
  const { customerId } = useParams<{ customerId: string }>()
  const dispatch = useDispatch()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const { isAuthenticated } = useAuthSession()

  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [modalState, setModalState] = useState<ModalState | null>(null)
  const [isSendTestOpen, setIsSendTestOpen] = useState<boolean>(false)
  const [selectedDelivery, setSelectedDelivery] = useState<WebhookDelivery | null>(null)
  const [filters, setFilters] = useState<WebhookDeliveryFilterValues>(INITIAL_FILTERS)
  // Local, never Redux: the plaintext signing secret exists exactly once and anything dispatched
  // is printed by redux-logger and captured by Sentry. Component state dies with the component,
  // but this route (`/customers/:customerId` → `CustomerLayout` → this `webhooks` child) reuses
  // the same component instance across a customerId-only navigation, so the effect below clears
  // it explicitly on a customer change.
  const [createdSecret, setCreatedSecret] = useState<string | null>(null)

  const { paginationModel, limit, offset, setPaginationModel } = useGridPagination(
    webhookDeliveryPageSizeOptions,
  )

  const isSaving = useSelector(getWebhookSaving)
  const subscriptionsError = useSelector(getWebhookSubscriptionsError)
  const deliveriesError = useSelector(getWebhookDeliveriesError)

  const availableTabs = useMemo(() => {
    return WEBHOOKS_TABS.filter((tab) => {
      if (tab.value === 'subscriptions') {
        return canViewSubscriptions
      }

      return canViewDeliveries
    })
  }, [canViewSubscriptions, canViewDeliveries])

  // `useSendTestWebhookGate` evaluates the send against the products slice (CustomerLayout loads
  // it only with partner_products_view) and the subscriptions slice (loaded here only with
  // webhook_subscriptions_view). Without either the precondition is unknown, so the action is
  // hidden rather than offered and wrongly blocked or refused (spec §10.4).
  const canSendTest = canSendWebhooks && canViewProducts && canViewSubscriptions

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // This route reuses the same component instance across a customerId-only navigation, so every
  // piece of customer-scoped state has to be dropped explicitly or it bleeds into the next
  // customer's view: the plaintext secret, the delivery the details modal is showing, the subscription
  // the edit modal would submit an update for, and the delivery log itself (whose REQUEST keeps
  // its rows on purpose, for same-customer paging). Filters and pagination are deliberately NOT
  // reset — they are the admin's own view preferences, not another customer's data.
  useEffect(() => {
    setCreatedSecret(null)
    setSelectedDelivery(null)
    setModalState(null)
    setIsSendTestOpen(false)
    dispatch(ClearAdminWebhookDeliveries())
  }, [customerId, dispatch])

  // Clearing on a customer change is not enough on its own: `handleSubmit` awaits the create
  // dispatch, and this component survives that navigation, so a request issued for customer A
  // can resolve *after* the effect above has run and re-set A's secret on customer B's page.
  // The continuation compares its captured id against this live ref before applying anything.
  const currentCustomerIdRef = useRef<string | undefined>(customerId)

  useEffect(() => {
    currentCustomerIdRef.current = customerId
  }, [customerId])

  // Subscriptions only. The api_integration gate now comes from the products slice, which
  // CustomerLayout fetches on customer select — this page no longer asks for it.
  useEffect(() => {
    if (!isAuthenticated || !customerId || !canViewSubscriptions) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchWebhookSubscriptions.request({ partnerId: customerId }))
  }, [asyncDispatchWithErrorToast, isAuthenticated, customerId, canViewSubscriptions, refreshToken])

  // Deliveries. Reruns on every filter and page change; the saga's takeLatest discards
  // superseded runs. The `'any'` sentinel and an empty resourceId are mapped to undefined so
  // callApi's omitBy drops them.
  useEffect(() => {
    if (!isAuthenticated || !customerId || !canViewDeliveries) {
      return
    }

    asyncDispatchWithErrorToast(
      AdminFetchWebhookDeliveries.request({
        partnerId: customerId,
        subscriptionId: filters.subscriptionId === 'any' ? undefined : filters.subscriptionId,
        status: filters.status === 'any' ? undefined : filters.status,
        eventType: filters.eventType === 'any' ? undefined : filters.eventType,
        resourceId: filters.resourceId.trim() === '' ? undefined : filters.resourceId.trim(),
        limit,
        offset,
      }),
    )
  }, [
    asyncDispatchWithErrorToast,
    isAuthenticated,
    customerId,
    canViewDeliveries,
    filters,
    limit,
    offset,
    refreshToken,
  ])

  const handleFiltersChange = useCallback(
    (nextFilters: WebhookDeliveryFilterValues) => {
      setFilters(nextFilters)
      // Reset to page 0: leaving the page index untouched while the filtered total shrinks is
      // how a grid ends up showing an empty page 4 of a 2-page result.
      setPaginationModel({ ...paginationModel, page: 0 })
    },
    [paginationModel, setPaginationModel],
  )

  const handleClearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS)
    setPaginationModel({ ...paginationModel, page: 0 })
  }, [paginationModel, setPaginationModel])

  const hasFilters = useMemo(() => {
    return (
      filters.subscriptionId !== 'any' ||
      filters.status !== 'any' ||
      filters.eventType !== 'any' ||
      filters.resourceId.trim() !== ''
    )
  }, [filters])

  const handleOpenCreate = useCallback(() => {
    setModalState({ mode: 'create', subscription: null })
  }, [])

  const handleOpenEdit = useCallback((subscription: WebhookSubscription) => {
    setModalState({ mode: 'edit', subscription })
  }, [])

  const handleCloseModal = useCallback(() => {
    setModalState(null)
  }, [])

  const handleOpenSendTest = useCallback(() => {
    setIsSendTestOpen(true)
  }, [])

  const handleCloseSendTest = useCallback(() => {
    setIsSendTestOpen(false)
  }, [])

  const handleCloseSecret = useCallback(() => {
    setCreatedSecret(null)
  }, [])

  const handleCloseDeliveryDetails = useCallback(() => {
    setSelectedDelivery(null)
  }, [])

  const handleSubmit = useCallback(
    async (values: WebhookSubscriptionFormValues) => {
      if (!modalState || !customerId) {
        return
      }

      const callbackUrl = values.callbackUrl.trim()

      if (modalState.mode === 'edit') {
        if (!modalState.subscription) {
          return
        }

        const updated = await asyncDispatchWithErrorToast<WebhookSubscription>(
          AdminUpdateWebhookSubscription.request({
            subscriptionId: modalState.subscription.id,
            callbackUrl,
            events: values.events,
            webhookVersion: values.webhookVersion,
          }),
        )

        if (!updated) {
          return
        }

        snackbar?.enqueueSnackbar('Webhook subscription updated', { variant: 'success' })
        setModalState(null)
        handleRefresh()

        return
      }

      const created = await asyncDispatchWithErrorToast<CreateWebhookSubscriptionResult>(
        AdminCreateWebhookSubscription.request({
          partnerId: customerId,
          callbackUrl,
          events: values.events,
          webhookVersion: values.webhookVersion,
        }),
      )

      if (!created) {
        return
      }

      // Dropped if the admin moved to another customer while this create was in flight —
      // otherwise this continuation would put customer A's signing secret on customer B's page.
      if (currentCustomerIdRef.current !== customerId) {
        return
      }

      // The secret arrives only as the resolved value of the create action, never as state.
      // No success toast here: the secret modal opens on `createdSecret` and is the confirmation.
      // A toast would sit on top of the one thing the admin has to read and copy.
      setCreatedSecret(created.secret ?? null)
      setModalState(null)
      handleRefresh()
    },
    [modalState, customerId, asyncDispatchWithErrorToast, snackbar, handleRefresh],
  )

  const handleToggleSubscription = useCallback(
    async (subscription: WebhookSubscription) => {
      // Only `isActive` is sent — every field on the backend body is optional, and there is no
      // delete endpoint, so this is the only way to retire a subscription.
      const updated = await asyncDispatchWithErrorToast<WebhookSubscription>(
        AdminUpdateWebhookSubscription.request({
          subscriptionId: subscription.id,
          isActive: !subscription.isActive,
        }),
      )

      if (!updated) {
        return
      }

      snackbar?.enqueueSnackbar(
        subscription.isActive ? 'Webhook subscription disabled' : 'Webhook subscription enabled',
        { variant: 'success' },
      )
      handleRefresh()
    },
    [asyncDispatchWithErrorToast, snackbar, handleRefresh],
  )

  const handleSendTest = useCallback(
    async (values: SendTestWebhookFormValues) => {
      if (!customerId || values.eventType === '') {
        return
      }

      const result = await asyncDispatchWithErrorToast<SendWebhookResult>(
        AdminSendWebhook.request({
          partnerId: customerId,
          eventType: values.eventType as WebhookEventType,
          resourceId: values.resourceId.trim(),
          resourceType: values.resourceType,
        }),
      )

      if (!result) {
        return
      }

      // The endpoint answers 200 with `{ success: false }` when the event row was written but the
      // outbound POST did not land, so the variant has to come from `success` — a green toast
      // there would tell the admin a live send worked when it did not. The backend's own message
      // is preferred; the fallbacks only cover an absent one, which would render an empty toast.
      const fallbackMessage = result.success
        ? 'Test webhook sent'
        : 'The event was recorded, but the webhook could not be delivered'

      snackbar?.enqueueSnackbar(result.message || fallbackMessage, {
        variant: result.success ? 'success' : 'warning',
      })
      handleRefresh()
    },
    [customerId, asyncDispatchWithErrorToast, snackbar, handleRefresh],
  )

  return (
    <Fragment>
      <div data-testid="admin-customer-webhooks-page" css={tw`min-w-0 flex-1 p-6`}>
        <div css={tw`mb-5 flex h-9 flex-row items-center justify-between gap-4`}>
          <Typography variant="h2">Webhooks</Typography>
          {/* One CTA per tab: create a subscription, or send a test event whose delivery will
              show up in the Deliveries list right below. */}
          {selectedTab === 'subscriptions' && canManageSubscriptions ? (
            <Button
              size="small"
              color="dark"
              className={tw`px-4`}
              leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
              onClick={handleOpenCreate}
              data-testid="webhook-subscriptions-add-button"
            >
              New subscription
            </Button>
          ) : null}
          {selectedTab === 'deliveries' && canSendTest ? (
            <Button
              size="small"
              color="dark"
              className={tw`px-4`}
              leftIcon={<GSIcon iconName="gs_lightning_bolt" size={20} css={tw`text-shades-0`} />}
              onClick={handleOpenSendTest}
              data-testid="webhook-send-test-button"
            >
              Send test event
            </Button>
          ) : null}
        </div>

        <div css={tw`flex flex-col gap-6`}>
          <TabsSelector<WebhooksTab>
            options={availableTabs}
            selectedValue={selectedTab}
            onSelect={setSelectedTab}
            testIdPrefix="admin-customer-webhooks"
          />

          {selectedTab === 'subscriptions' ? (
            <div>
              {subscriptionsError ? (
                <Typography
                  variant="body2"
                  css={tw`mb-4 rounded-lg bg-critical-100 p-3 text-critical-500`}
                  data-testid="webhook-subscriptions-error"
                >
                  {subscriptionsError}
                </Typography>
              ) : null}
              <WebhookSubscriptionsTableToolbar onRefresh={handleRefresh} />
              <WebhookSubscriptionsTable
                canManage={canManageSubscriptions}
                onEditSubscription={handleOpenEdit}
                onToggleSubscription={handleToggleSubscription}
              />
            </div>
          ) : null}

          {selectedTab === 'deliveries' ? (
            <div>
              {/* Above the filter bar, which is the table's own header — a banner between the
                  two would split the card. */}
              {deliveriesError ? (
                <Typography
                  variant="body2"
                  css={tw`mb-4 rounded-lg bg-critical-100 p-3 text-critical-500`}
                  data-testid="webhook-deliveries-error"
                >
                  {deliveriesError}
                </Typography>
              ) : null}
              <WebhookDeliveriesFilters
                filters={filters}
                onChange={handleFiltersChange}
                onClear={handleClearFilters}
              />
              <WebhookDeliveriesTable
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                hasFilters={hasFilters}
                onViewDelivery={setSelectedDelivery}
              />
            </div>
          ) : null}
        </div>
      </div>

      <WebhookSubscriptionFormModal
        isOpen={Boolean(modalState)}
        mode={modalState?.mode ?? 'create'}
        subscription={modalState?.subscription ?? null}
        isSaving={isSaving}
        onSubmit={handleSubmit}
        onClose={handleCloseModal}
      />
      <WebhookSecretModal secret={createdSecret} onClose={handleCloseSecret} />
      <SendTestWebhookModal
        isOpen={isSendTestOpen}
        onClose={handleCloseSendTest}
        onSend={handleSendTest}
      />
      <WebhookDeliveryDetailsModal
        delivery={selectedDelivery}
        onClose={handleCloseDeliveryDetails}
      />
    </Fragment>
  )
}

export const CustomerWebhooksPage = memo(CustomerWebhooksPageComponent)
